import React, { useEffect, useState } from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { KlpModal } from '@klp/design';
import { ActionButton } from '@klp/design/Action';
import { useRoleMapping } from '@klp/leverandor/hooks';
import { useDispatch, useSelector } from 'react-redux';
import {
    leverandorActions,
    leverandorSelectors,
    isActiveModal,
} from '../../store/leverandorSlice';
import {
    useSendNotificationWithCommentMutation,
    updateLeverandorProcessFormfieldsCache,
} from '../../services/rtkApi';
import { showToast } from '@klpkonsern/ui';
import { CamundaFields } from '@forvalter/utils/CamundaUtils';
import type { FormField } from '@klp/formgenerator/interfaces';
import { useRolleKommentar } from './hooks/useRolleKommentar';

export const SendKommentarModal = () => {
    const { t, i18n } = useTranslation();
    const { handleMapping: handleRoleMapping } = useRoleMapping();
    const dispatch = useDispatch();
    const { activeModal } = useSelector(leverandorSelectors);
    const sendKommentarModalData = isActiveModal(activeModal, 'sendKommentar')
        ? activeModal
        : undefined;

    const [rolle, setRolle] = useState('');
    const [comment, setComment] = useState('');

    useEffect(() => {
        if (sendKommentarModalData) {
            setRolle(sendKommentarModalData.defaultRolle ?? '');
        }
    }, [
        sendKommentarModalData?.prosessInstansId,
        sendKommentarModalData?.defaultRolle,
    ]);

    const activity = sendKommentarModalData?.activity;
    const { attachComment, isRolleKommentarerFieldInTheForm } =
        useRolleKommentar(activity ?? ({ formFields: [] } as any));

    const [sendNotification, { isLoading: isSending }] =
        useSendNotificationWithCommentMutation();

    const isLoading = isSending;

    const handleClose = () => {
        setRolle('');
        setComment('');
        dispatch(leverandorActions.setActiveModal(undefined));
    };

    const handleSend = async () => {
        if (!sendKommentarModalData?.prosessInstansId || !rolle || !comment) {
            return;
        }

        try {
            let rolleKommentarer: string | undefined;
            let updatedFormFields: Array<FormField> | undefined;

            if (activity?.id && isRolleKommentarerFieldInTheForm) {
                rolleKommentarer = attachComment(comment, rolle);
                updatedFormFields = (activity.formFields ?? []).map((field) =>
                    field.id === CamundaFields.ROLLE_KOMMENTARER
                        ? { ...field, value: rolleKommentarer }
                        : field,
                );
            }

            await sendNotification({
                prosessInstansId: sendKommentarModalData.prosessInstansId,
                rolle,
                comment,
                rolleKommentarer,
            }).unwrap();

            if (updatedFormFields && activity?.id) {
                dispatch(
                    updateLeverandorProcessFormfieldsCache(
                        sendKommentarModalData.prosessInstansId,
                        i18n.language,
                        updatedFormFields,
                        activity.id,
                    ),
                );
            }

            showToast({
                body: t('leverandor.successfulOperation'),
                type: 'success',
            });
            handleClose();
        } catch {
            showToast({
                body: t('general.error.message'),
                type: 'error',
            });
        }
    };

    const roller = sendKommentarModalData?.roller ?? [];

    return (
        <KlpModal
            isOpen={!!sendKommentarModalData}
            onClose={handleClose}
            titleString={t('leverandor.sendKommentar.title')}
        >
            <Box mb={2}>
                <FormControl fullWidth={true}>
                    <InputLabel id="role-label">
                        {t('leverandor.sendKommentar.selectRole')}
                    </InputLabel>
                    <Select
                        labelId="role-label"
                        value={rolle}
                        onChange={(e) => setRolle(e.target.value as string)}
                    >
                        {roller.map((r) => (
                            <MenuItem key={r} value={r}>
                                {handleRoleMapping(r)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Box mb={2}>
                <TextField
                    fullWidth={true}
                    multiline={true}
                    label={t('leverandor.sendKommentar.commentLabel')}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                />
            </Box>

            <Box
                display="flex"
                justifyContent="flex-end"
                style={{ gap: '8px' }}
            >
                <ActionButton
                    actionType="cancel"
                    onClick={handleClose}
                    disabled={isLoading}
                    translation="general.cancel"
                />
                <ActionButton
                    actionType="submit"
                    onClick={handleSend}
                    isLoading={isLoading}
                    disabled={!rolle || !comment}
                    translation="leverandor.sendKommentar.send"
                />
            </Box>
        </KlpModal>
    );
};
